import React from 'react';
import { FaUsers, FaBook, FaLandmark, FaTrophy, FaMicrophoneAlt, FaMosque, FaUserCog, FaLayerGroup, FaComments, FaChartLine, FaHeart, FaMedal, FaGlobeAsia, FaInstagram, FaFacebook, FaYoutube, FaTwitter } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const About: React.FC = () => {
  return (
    <>
      <SEO 
        title="About پیروز Pirooz | Our Mission to Teach Farsi Persian Language"
        description="Learn about پیروز Pirooz's mission to make Farsi (Persian) language learning accessible worldwide. Discover our story, team, and commitment to preserving Iranian culture through education."
        keywords={[
          'about vajak', 'farsi learning mission', 'persian language education', 'iranian culture',
          'farsi teachers', 'persian language platform', 'language learning company', 'farsi education'
        ]}
        canonicalUrl="/about"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About پیروز Pirooz",
          "description": "Learn about Vajak's mission to make Farsi language learning accessible worldwide",
          "url": "https://farsi-app.com/about",
          "mainEntity": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz",
            "description": "Online Farsi (Persian) language learning platform",
            "foundingDate": "2024",
            "mission": "To make Farsi language learning accessible to everyone worldwide while preserving Iranian culture",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            }
          }
        }}
      />
      <div className="bg-gray-50 min-h-screen font-sans">
      {/* Hero Section */}
      <section className="text-white py-20" style={{ background: 'linear-gradient(135deg, #6B46C1 0%, #4299E1 100%)' }}>
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-10 md:mb-0">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Noto Nastaliq Urdu, serif' }}></h1>
                          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center md:text-left text-white">Pirooz</h2>
            <p className="text-xl mb-8 text-center md:text-left">
              Empowering learners worldwide to master Farsi through innovative technology, cultural immersion, and personalized learning experiences.
          </p>
            <div className="flex justify-center md:justify-start space-x-4">
              <a href="/alphabet-learning" className="bg-white text-indigo-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition duration-300">Start Learning</a>
              <a href="/pathways" className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-indigo-700 transition duration-300">Explore More</a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-800">Our Mission</h2>
          <div className="flex flex-col md:flex-row items-center mb-16">
            <div className="md:w-1/2 md:pr-12 mx-auto">
              <p className="text-lg text-gray-700 mb-6 text-left">
                At Pirooz, we believe that language learning should be accessible, engaging, and culturally rich. Our mission is to break down barriers to Farsi learning by providing a comprehensive platform that combines modern technology with traditional Persian wisdom.
              </p>
              <p className="text-lg text-gray-700 text-left">
                We're not just teaching vocabulary and grammar - we're opening doors to a rich cultural heritage that spans thousands of years, from the poetry of Hafez to the modern streets of Tehran.
              </p>
            </div>
          </div>
          <div className="bg-indigo-50 rounded-xl p-8 shadow-inner">
            <h3 className="text-2xl font-semibold text-center mb-8 text-indigo-700">Join thousands of learners discovering the beauty of Farsi</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Link to="/users" className="bg-white p-6 rounded-lg shadow text-center transition-transform duration-300 hover:scale-105 hover:shadow-lg cursor-pointer">
                <div className="text-indigo-600 text-4xl mb-2 flex justify-center"><FaUsers /></div>
                <h4 className="text-xl font-bold mb-1">10,000+</h4>
                <p className="text-gray-600">Active Learners</p>
              </Link>
              <Link to="/vocabulary" className="bg-white p-6 rounded-lg shadow text-center transition-transform duration-300 hover:scale-105 hover:shadow-lg cursor-pointer">
                <div className="text-indigo-600 text-4xl mb-2 flex justify-center"><FaBook /></div>
                <h4 className="text-xl font-bold mb-1">500+</h4>
                <p className="text-gray-600">Vocabulary Words</p>
              </Link>
              <Link to="/cultural" className="bg-white p-6 rounded-lg shadow text-center transition-transform duration-300 hover:scale-105 hover:shadow-lg cursor-pointer">
                <div className="text-indigo-600 text-4xl mb-2 flex justify-center"><FaLandmark /></div>
                <h4 className="text-xl font-bold mb-1">50+</h4>
                <p className="text-gray-600">Cultural Lessons</p>
              </Link>
              <Link to="/success-stories" className="bg-white p-6 rounded-lg shadow text-center transition-transform duration-300 hover:scale-105 hover:shadow-lg cursor-pointer">
                <div className="text-indigo-600 text-4xl mb-2 flex justify-center"><FaTrophy /></div>
                <h4 className="text-xl font-bold mb-1">95%</h4>
                <p className="text-gray-600">Success Rate</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">Why Choose Pirooz?</h2>
          <p className="text-xl text-center mb-16 text-gray-600 max-w-3xl mx-auto">Discover the unique features that make our platform stand out</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link to="/vocabulary-garden" className="bg-white p-8 rounded-xl shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
              <div className="text-indigo-600 text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform"><FaMicrophoneAlt /></div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-600 transition-colors">Native speaker content</h3>
              <p className="text-gray-700">Learn authentic pronunciation and usage from native Farsi speakers with clear audio recordings.</p>
            </Link>
            <Link to="/cultural" className="bg-white p-8 rounded-xl shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
              <div className="text-indigo-600 text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform"><FaMosque /></div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-600 transition-colors">Cultural immersion</h3>
              <p className="text-gray-700">Explore Persian culture through poetry, traditions, and real-life scenarios that bring the language to life.</p>
            </Link>
            <Link to="/pathways" className="bg-white p-8 rounded-xl shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
              <div className="text-indigo-600 text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform"><FaUserCog /></div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-600 transition-colors">Personalized learning</h3>
              <p className="text-gray-700">Our adaptive system creates customized learning pathways based on your goals and progress.</p>
            </Link>
            <Link to="/flashcards" className="bg-white p-8 rounded-xl shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
              <div className="text-indigo-600 text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform"><FaLayerGroup /></div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-600 transition-colors">Interactive tools</h3>
              <p className="text-gray-700">Engage with flashcards, quizzes, and spaced repetition to reinforce your learning effectively.</p>
            </Link>
            <Link to="/ai-chat" className="bg-white p-8 rounded-xl shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
              <div className="text-indigo-600 text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform"><FaComments /></div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-600 transition-colors">Supportive community</h3>
              <p className="text-gray-700">Connect with fellow learners, ask questions, and practice with native speakers in our community.</p>
            </Link>
            <Link to="/progress" className="bg-white p-8 rounded-xl shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
              <div className="text-indigo-600 text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform"><FaChartLine /></div>
              <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-600 transition-colors">Progress tracking</h3>
              <p className="text-gray-700">Monitor your improvement with detailed statistics and personalized feedback on your learning journey.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-800">Our Values</h2>
          <p className="text-xl text-center mb-16 text-gray-600 max-w-3xl mx-auto">The principles that guide everything we do at Vajak</p>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="flex">
              <div className="mr-6">
                <div className="bg-indigo-100 text-indigo-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl">
                  <FaHeart />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-gray-800 text-left">Passion for Learning</h3>
                <p className="text-gray-700 text-left">We believe everyone deserves access to quality language education that inspires and empowers.</p>
              </div>
            </div>
            <div className="flex">
              <div className="mr-6">
                <div className="bg-indigo-100 text-indigo-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl">
                  <FaMedal />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-gray-800 text-left">Educational Excellence</h3>
                <p className="text-gray-700 text-left">Our content is crafted by native speakers and language experts to ensure authentic, effective learning.</p>
              </div>
            </div>
            <div className="flex">
              <div className="mr-6">
                <div className="bg-indigo-100 text-indigo-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl">
                  <FaUsers />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-gray-800 text-left">Community First</h3>
                <p className="text-gray-700 text-left">We foster a supportive community where learners can connect, share, and grow together.</p>
              </div>
          </div>
            <div className="flex">
              <div className="mr-6">
                <div className="bg-indigo-100 text-indigo-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl">
                  <FaGlobeAsia />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3 text-gray-800 text-left">Cultural Immersion</h3>
                <p className="text-gray-700 text-left">Language learning goes beyond words - we immerse you in Persian culture, history, and traditions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #6B46C1 0%, #4299E1 100%)' }}>
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-800">Meet Our Team</h2>
          <p className="text-xl text-center mb-16 text-gray-600 max-w-3xl mx-auto">The passionate individuals behind Vajak's mission to make Farsi learning accessible to everyone</p>
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-md overflow-hidden transition-transform duration-300 hover:scale-105">
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:mr-6 mb-6 md:mb-0 overflow-hidden rounded-full w-36 h-36 border-4 border-indigo-100 shadow-lg">
                  <img src="/images/profiles/negin-nickparsa.jpg" alt="Negin Nickparsa - Founder & CEO of Vajak" className="w-full h-full object-cover object-center transition duration-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1 text-gray-800 text-left">Negin Nickparsa</h3>
                  <p className="text-indigo-600 font-medium mb-3 text-left">Founder & CEO</p>
                  <p className="text-gray-700 text-left">Passionate about making Farsi learning accessible to everyone through innovative technology and cultural immersion.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md overflow-hidden transition-transform duration-300 hover:scale-105">
              <div className="text-center">
                <div>
                  <h3 className="text-xl font-bold mb-1 text-gray-800">Our Team</h3>
                  <p className="text-indigo-600 font-medium mb-3">Language Experts</p>
                  <p className="text-gray-700">Native Farsi speakers and language education specialists dedicated to creating authentic learning experiences.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 text-white" style={{ background: 'linear-gradient(135deg, #6B46C1 0%, #4299E1 100%)' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-white">🌟 Our Story</h2>
            <p className="text-lg mb-6 text-white">
              Pirooz was born from a simple belief: that learning Farsi should be as beautiful and enriching as the language itself. Founded by Negin Nickparsa, a passionate educator and native Farsi speaker, our platform combines traditional Persian wisdom with modern learning technology.
            </p>
            <h3 className="text-2xl font-bold mb-4 text-white">From Vision to Reality</h3>
            <p className="text-lg mb-6 text-white">
              What started as a personal project to help friends learn Farsi has grown into a global community of learners, educators, and cultural enthusiasts. Our journey has been guided by feedback from thousands of users who share our passion for Persian language and culture.
            </p>
            <p className="text-lg text-white italic">
              Today, Vajak serves learners from over 50 countries, offering comprehensive Farsi education that goes beyond traditional language learning to embrace the rich cultural heritage of Persia.
            </p>
          </div>
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold mb-6 text-white">Innovation meets tradition in every lesson</h3>
            <a href="/alphabet-learning" className="inline-block bg-white text-indigo-700 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition duration-300 shadow-lg">Start Your Journey Today</a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">Join Our Community</h2>
          <p className="text-xl mb-10 text-gray-600 max-w-2xl mx-auto">Start your Farsi learning journey today and discover the beauty of Persian language and culture</p>
          <a href="/alphabet-learning" className="inline-block bg-indigo-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-indigo-700 transition duration-300 shadow-lg">Start Learning Free</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Noto Nastaliq Urdu, serif' }}></h3>
              <p className="text-gray-400">Empowering learners worldwide to master Farsi through innovative technology and cultural immersion.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">🚀 Features</h4>
              <ul className="space-y-2">
                <li><a href="/pathways" className="text-gray-400 hover:text-white transition">Explore our learning tools</a></li>
                <li><a href="/events" className="text-gray-400 hover:text-white transition">Events</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">💰 Pricing</h4>
              <ul className="space-y-2">
                <li><a href="/pricing" className="text-gray-400 hover:text-white transition">Choose your plan</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Help</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-gray-400 hover:text-amber-400">Contact</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-amber-400">Contact Us</Link></li>
                <li><a href="/success-stories" className="text-gray-400 hover:text-white transition">Success Stories</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">© 2024 پیروز Pirooz. All rights reserved.</p>
            <div className="flex space-x-6">
              <a href="https://twitter.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition"><FaTwitter className="text-xl" /></a>
              <a href="https://instagram.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition"><FaInstagram className="text-xl" /></a>
              <a href="https://facebook.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition"><FaFacebook className="text-xl" /></a>
              <a href="https://youtube.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition"><FaYoutube className="text-xl" /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default About; 